'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const sides = [
    {
        role: 'Students',
        tagline: 'Learn without limits.',
        description: 'Access courses, live classes, educational content, assessments, tutors, and learning communities.',
        accent: '#3757FF',
        bg: 'rgba(55,87,255,0.04)',
        border: 'rgba(55,87,255,0.12)',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M14 4L4 9l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
                <path d="M4 14l10 5 10-5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4 19l10 5 10-5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        features: ['Live classes', 'Course library', 'Progress tracking', 'Tutor discovery', 'Learning communities'],
        size: 'large',
    },
    {
        role: 'Tutors',
        tagline: 'Turn knowledge into opportunity.',
        description: 'Create programs, manage availability, teach students, conduct live sessions, and earn through tutoring.',
        accent: '#2A44CC',
        bg: 'rgba(42,68,204,0.04)',
        border: 'rgba(42,68,204,0.12)',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="9" r="5" stroke="currentColor" strokeWidth="1.75" />
                <path d="M5 24c0-4.418 4.03-8 9-8s9 3.582 9 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M20 4l2 2-5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        features: ['Program creation', 'Availability management', 'Live sessions', 'Student management', 'Earnings dashboard'],
        size: 'small',
    },
    {
        role: 'Parents',
        tagline: 'Stay connected to learning.',
        description: 'Follow academic progress and remain informed about a student\'s educational experience.',
        accent: '#4A6AFF',
        bg: 'rgba(74,106,255,0.04)',
        border: 'rgba(74,106,255,0.12)',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M14 3C8.477 3 4 7.477 4 13c0 2.136.67 4.116 1.81 5.74L4 25l6.26-1.81A9.96 9.96 0 0014 24c5.523 0 10-4.477 10-10S19.523 3 14 3z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
                <path d="M10 13h8M10 17h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
        ),
        features: ['Progress visibility', 'Tutor communication', 'Activity feed', 'Academic reports', 'Notifications'],
        size: 'small',
    },
    {
        role: 'Institutions',
        tagline: 'Run education from one place.',
        description: 'Manage students, staff, programs, schedules, assessments, analytics, and institutional operations.',
        accent: '#1A35CC',
        bg: 'rgba(26,53,204,0.04)',
        border: 'rgba(26,53,204,0.12)',
        icon: (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <rect x="3" y="10" width="22" height="15" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M9 10V7a5 5 0 0110 0v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M14 16v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <circle cx="14" cy="15" r="1.5" fill="currentColor" />
            </svg>
        ),
        features: ['Student management', 'Staff operations', 'Analytics & reporting', 'Billing & finance', 'Institutional identity'],
        size: 'large',
    },
];

export default function FourSidesSection() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);
    const [activeCard, setActiveCard] = useState<number | null>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold: 0.1 }
        );
        if (sectionRef?.current) observer?.observe(sectionRef?.current);
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            id="platform"
            ref={sectionRef}
            className="py-28 px-6 md:px-10"
            style={{ background: '#FAFAFA' }}
        >
            <div className="max-w-7xl mx-auto">
                {/* Section header */}
                <div className="max-w-2xl mb-20">
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                        style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                    >
                        The four sides of Wisly
                    </div>
                    <h2
                        className="font-display font-800 mb-5"
                        style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 1.05, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                    >
                        One platform.
                        <br />
                        Four connected experiences.
                    </h2>
                    <p className="text-lg leading-relaxed" style={{ color: '#666' }}>
                        Wisly is built around the relationships that make education work — not just the content.
                    </p>
                </div>

                {/* Asymmetric bento grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {/* Students — large, spans 2 cols on lg */}
                    <div
                        className={`lg:col-span-2 rounded-3xl p-8 md:p-10 cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                        style={{
                            background: activeCard === 0 ? BRAND : sides?.[0]?.bg,
                            border: `1px solid ${activeCard === 0 ? BRAND : sides?.[0]?.border}`,
                            transitionDelay: '0ms',
                            boxShadow: activeCard === 0 ? '0 24px 64px rgba(55,87,255,0.25)' : 'none',
                        }}
                        onMouseEnter={() => setActiveCard(0)}
                        onMouseLeave={() => setActiveCard(null)}
                    >
                        <div className="flex flex-col h-full gap-6">
                            <div className="flex items-start justify-between">
                                <div
                                    className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300"
                                    style={{
                                        background: activeCard === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(55,87,255,0.1)',
                                        color: activeCard === 0 ? 'white' : BRAND,
                                    }}
                                >
                                    {sides?.[0]?.icon}
                                </div>
                                <span
                                    className="text-xs font-700 uppercase tracking-widest px-3 py-1.5 rounded-full"
                                    style={{
                                        background: activeCard === 0 ? 'rgba(255,255,255,0.15)' : 'rgba(55,87,255,0.08)',
                                        color: activeCard === 0 ? 'white' : BRAND,
                                        letterSpacing: '0.12em',
                                    }}
                                >
                                    {sides?.[0]?.role}
                                </span>
                            </div>
                            <div>
                                <h3
                                    className="font-display font-800 mb-3 transition-colors duration-300"
                                    style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', letterSpacing: '-0.02em', color: activeCard === 0 ? 'white' : '#0D0D0D' }}
                                >
                                    {sides?.[0]?.tagline}
                                </h3>
                                <p className="text-base leading-relaxed transition-colors duration-300" style={{ color: activeCard === 0 ? 'rgba(255,255,255,0.8)' : '#666' }}>
                                    {sides?.[0]?.description}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-auto">
                                {sides?.[0]?.features?.map(f => (
                                    <span
                                        key={f}
                                        className="text-xs font-600 px-3 py-1.5 rounded-full transition-all duration-300"
                                        style={{
                                            background: activeCard === 0 ? 'rgba(255,255,255,0.15)' : 'rgba(55,87,255,0.06)',
                                            color: activeCard === 0 ? 'white' : '#444',
                                            border: `1px solid ${activeCard === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(55,87,255,0.1)'}`,
                                        }}
                                    >
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Tutors */}
                    <div
                        className={`rounded-3xl p-8 cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                        style={{
                            background: activeCard === 1 ? sides?.[1]?.accent : sides?.[1]?.bg,
                            border: `1px solid ${activeCard === 1 ? sides?.[1]?.accent : sides?.[1]?.border}`,
                            transitionDelay: '80ms',
                            boxShadow: activeCard === 1 ? `0 24px 64px rgba(42,68,204,0.25)` : 'none',
                        }}
                        onMouseEnter={() => setActiveCard(1)}
                        onMouseLeave={() => setActiveCard(null)}
                    >
                        <div className="flex flex-col h-full gap-5">
                            <div
                                className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300"
                                style={{ background: activeCard === 1 ? 'rgba(255,255,255,0.2)' : 'rgba(42,68,204,0.1)', color: activeCard === 1 ? 'white' : sides?.[1]?.accent }}
                            >
                                {sides?.[1]?.icon}
                            </div>
                            <div>
                                <span className="text-xs font-700 uppercase tracking-widest mb-3 block" style={{ color: activeCard === 1 ? 'rgba(255,255,255,0.7)' : '#888', letterSpacing: '0.12em' }}>{sides?.[1]?.role}</span>
                                <h3 className="font-display font-800 mb-2 transition-colors duration-300" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em', color: activeCard === 1 ? 'white' : '#0D0D0D' }}>
                                    {sides?.[1]?.tagline}
                                </h3>
                                <p className="text-sm leading-relaxed transition-colors duration-300" style={{ color: activeCard === 1 ? 'rgba(255,255,255,0.8)' : '#666' }}>
                                    {sides?.[1]?.description}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-auto">
                                {sides?.[1]?.features?.map(f => (
                                    <span key={f} className="text-xs font-600 px-3 py-1.5 rounded-full transition-all duration-300"
                                        style={{ background: activeCard === 1 ? 'rgba(255,255,255,0.15)' : 'rgba(42,68,204,0.06)', color: activeCard === 1 ? 'white' : '#444', border: `1px solid ${activeCard === 1 ? 'rgba(255,255,255,0.2)' : 'rgba(42,68,204,0.1)'}` }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Parents */}
                    <div
                        className={`rounded-3xl p-8 cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                        style={{
                            background: activeCard === 2 ? sides?.[2]?.accent : sides?.[2]?.bg,
                            border: `1px solid ${activeCard === 2 ? sides?.[2]?.accent : sides?.[2]?.border}`,
                            transitionDelay: '160ms',
                            boxShadow: activeCard === 2 ? `0 24px 64px rgba(74,106,255,0.25)` : 'none',
                        }}
                        onMouseEnter={() => setActiveCard(2)}
                        onMouseLeave={() => setActiveCard(null)}
                    >
                        <div className="flex flex-col h-full gap-5">
                            <div
                                className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300"
                                style={{ background: activeCard === 2 ? 'rgba(255,255,255,0.2)' : 'rgba(74,106,255,0.1)', color: activeCard === 2 ? 'white' : sides?.[2]?.accent }}
                            >
                                {sides?.[2]?.icon}
                            </div>
                            <div>
                                <span className="text-xs font-700 uppercase tracking-widest mb-3 block" style={{ color: activeCard === 2 ? 'rgba(255,255,255,0.7)' : '#888', letterSpacing: '0.12em' }}>{sides?.[2]?.role}</span>
                                <h3 className="font-display font-800 mb-2 transition-colors duration-300" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em', color: activeCard === 2 ? 'white' : '#0D0D0D' }}>
                                    {sides?.[2]?.tagline}
                                </h3>
                                <p className="text-sm leading-relaxed transition-colors duration-300" style={{ color: activeCard === 2 ? 'rgba(255,255,255,0.8)' : '#666' }}>
                                    {sides?.[2]?.description}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-auto">
                                {sides?.[2]?.features?.map(f => (
                                    <span key={f} className="text-xs font-600 px-3 py-1.5 rounded-full transition-all duration-300"
                                        style={{ background: activeCard === 2 ? 'rgba(255,255,255,0.15)' : 'rgba(74,106,255,0.06)', color: activeCard === 2 ? 'white' : '#444', border: `1px solid ${activeCard === 2 ? 'rgba(255,255,255,0.2)' : 'rgba(74,106,255,0.1)'}` }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Institutions — large, spans 2 cols on lg */}
                    <div
                        className={`lg:col-span-2 rounded-3xl p-8 md:p-10 cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                        style={{
                            background: activeCard === 3 ? sides?.[3]?.accent : sides?.[3]?.bg,
                            border: `1px solid ${activeCard === 3 ? sides?.[3]?.accent : sides?.[3]?.border}`,
                            transitionDelay: '240ms',
                            boxShadow: activeCard === 3 ? `0 24px 64px rgba(26,53,204,0.25)` : 'none',
                        }}
                        onMouseEnter={() => setActiveCard(3)}
                        onMouseLeave={() => setActiveCard(null)}
                    >
                        <div className="flex flex-col h-full gap-6">
                            <div className="flex items-start justify-between">
                                <div
                                    className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300"
                                    style={{ background: activeCard === 3 ? 'rgba(255,255,255,0.2)' : 'rgba(26,53,204,0.1)', color: activeCard === 3 ? 'white' : sides?.[3]?.accent }}
                                >
                                    {sides?.[3]?.icon}
                                </div>
                                <span
                                    className="text-xs font-700 uppercase tracking-widest px-3 py-1.5 rounded-full"
                                    style={{ background: activeCard === 3 ? 'rgba(255,255,255,0.15)' : 'rgba(26,53,204,0.08)', color: activeCard === 3 ? 'white' : sides?.[3]?.accent, letterSpacing: '0.12em' }}
                                >
                                    {sides?.[3]?.role}
                                </span>
                            </div>
                            <div>
                                <h3
                                    className="font-display font-800 mb-3 transition-colors duration-300"
                                    style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', letterSpacing: '-0.02em', color: activeCard === 3 ? 'white' : '#0D0D0D' }}
                                >
                                    {sides?.[3]?.tagline}
                                </h3>
                                <p className="text-base leading-relaxed transition-colors duration-300" style={{ color: activeCard === 3 ? 'rgba(255,255,255,0.8)' : '#666' }}>
                                    {sides?.[3]?.description}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-auto">
                                {sides?.[3]?.features?.map(f => (
                                    <span key={f} className="text-xs font-600 px-3 py-1.5 rounded-full transition-all duration-300"
                                        style={{ background: activeCard === 3 ? 'rgba(255,255,255,0.15)' : 'rgba(26,53,204,0.06)', color: activeCard === 3 ? 'white' : '#444', border: `1px solid ${activeCard === 3 ? 'rgba(255,255,255,0.2)' : 'rgba(26,53,204,0.1)'}` }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
